import { useWindowSize } from "hooks/useWindowSize";
import classNames from "classnames";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import Loader from "components/DesignSystem/Loader/loader";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { convertDisplayToOptions } from "components/DesignSystem/Combobox/utils/convertDisplayToOptions";
import { getCurrentDisplayValue } from "components/DesignSystem/Combobox/utils/getCurrentDisplayValue";
import { DateField } from "components/DesignSystem/DateInput/DateField";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  Filter,
  MultiSelectFilter,
} from "components/DesignSystem/Filter/Filter";
import Modal from "components/DesignSystem/Modal/Modal";
import { Switch } from "components/DesignSystem/Switch/Switch";
import Table from "components/DesignSystem/Table/V2/Table";
import { ExpenseWaterfallDetailsModal } from "components/ExpenseWaterfallDetails/ExpenseWaterfallDetails";
import { ExportOptions } from "components/ExportOptions/ExportOptions";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import { FormikForm } from "components/FormikForm/FormikForm";
import { CaretDown } from "components/icons/CaretDown";
import { Info } from "components/icons/Info";
import { colors } from "constants/colors";
import { YYYY_MM_DD } from "constants/date";
import {
  REVENUE_DATA_SOURCE,
  REVENUE_DATA_SOURCE_OPTIONS,
  REVENUE_DATA_SOURCE_TYPES,
} from "constants/revenueDataSource";
import {
  EXPENSE_SCHEDULE_EXPORT_TYPES,
  EXPENSE_SCHEDULE_EXPORT_TYPES_DISPLAY,
  EXPENSE_SCHEDULE_EXPORT_TYPES_TYPE,
} from "constants/expenseScheduleExportTypes";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDownloadFile } from "hooks/useDownloadFile";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useCallback, useMemo, useState } from "react";
import {
  createColumnHelper,
  DisplayColumnDef,
  flexRender,
  getCoreRowModel,
  getGroupedRowModel,
  useReactTable,
} from "react-table-8.10.7";
import {
  ExpenseSchedule,
  useGetExpenseScheduleQuery,
  useLazyGetExpenseScheduleQuery,
} from "store/apis/expenseSchedule";
import { formatTimeToMonthYear } from "utils/formatDate";
import { getCommonPinningStyles } from "utils/getCommonPinningStyles";

type ExpenseWaterfallRow = ExpenseSchedule & {
  isTotal?: boolean;
};

const DESKTOP_PINNING = {
  left: ["period", "booked_expense", "recognized_total", "deferred_total"],
};

const NO_PINNING = {};

export const ExpenseWaterfall = () => {
  const { isMobile } = useWindowSize();
  const entityId = useCurrentEntityId();
  const groupId = useCurrentGroupContext().uuid;
  const [activeCellId, setActiveCellId] = useState<string | null>(null);

  const exportModal = useModal();

  const { values, updateFilter } = useFilters({
    initialValue: {
      START_DATE: dayjs().startOf("year").format(YYYY_MM_DD) as string,
      END_DATE: dayjs().endOf("month").format(YYYY_MM_DD) as string,
      SELECT_PERIOD: "CUSTOM" as string,
      INCLUDE_CARRY_FORWARD: false,
      SHOW_DEFERRALS: false,
      SOURCE: [] as REVENUE_DATA_SOURCE_TYPES[],
    },
  });

  const expenseSourceKeys: REVENUE_DATA_SOURCE_TYPES[] = [
    REVENUE_DATA_SOURCE.MANUAL,
    REVENUE_DATA_SOURCE.BILL_COM,
    REVENUE_DATA_SOURCE.JUSTPAID,
  ];

  const sourceOptions = expenseSourceKeys.map((key) => ({
    value: key,
    label: REVENUE_DATA_SOURCE_OPTIONS[key],
  }));

  const { data: expenseScheduleData } = useGetExpenseScheduleQuery(
    {
      entityId: entityId,
      start_date: values.START_DATE,
      end_date: values.END_DATE,
      carry_forward_bills: values.INCLUDE_CARRY_FORWARD,
      source: values.SOURCE,
    },
    {
      skip: !entityId,
    }
  );

  const [
    lazyGetExpenseSchedule,
    { isLoading: isExporting, isFetching: isFetchingExport },
  ] = useLazyGetExpenseScheduleQuery();
  const { successToast, alertToast } = useToast();
  const {
    downloadFile,
    result: { isLoading: isDownloading, isFetching: isFetchingDownload },
  } = useDownloadFile();

  // Get all unique periods from periodic_recognized_expense across all schedule items
  const allPeriods = useMemo(() => {
    if (!expenseScheduleData) return [];

    return expenseScheduleData.schedule.reduce<string[]>((acc, item) => {
      const periods =
        item.periodic_recognized_expense?.map((period) => period.period) || [];

      for (const period of periods) {
        if (!acc.includes(period)) {
          acc.push(period);
        }
      }
      return acc;
    }, []);
  }, [expenseScheduleData]);

  const startDate = expenseScheduleData?.schedule.at(0)?.period_start;

  // Prepare table data with totals row
  const tableData = useMemo((): ExpenseWaterfallRow[] => {
    if (!expenseScheduleData) return [];

    const scheduleRows = expenseScheduleData.schedule.map((item) => ({
      ...item,
      isTotal: false,
    }));

    // Create totals row
    const totalsRow: ExpenseWaterfallRow = {
      period: "Total",
      period_start: scheduleRows.at(0)?.period_start || "",
      period_end: scheduleRows.at(-1)?.period_end || "",
      booked_expense: {
        booked_from_this_period_sum: expenseScheduleData.total_booked_expense,
        booked_bill_ids: [],
      },
      recognized_expense: {
        recognized_from_this_month: 0,
        recognized_from_previous_months: 0,
        recognized_as_of_period_end:
          expenseScheduleData.total_recognized_expense,
        total_recognized: expenseScheduleData.total_recognized_expense,
        recognized_bill_ids_as_of_current_period: [],
      },
      deferred_expense: {
        deferred_from_this_month: 0,
        deferred_from_previous_months: 0,
        prev_deferred_and_recognized_in_current_month: 0,
        deferred_expense_as_of_period_end:
          expenseScheduleData.total_deferred_expense_as_of_end_date,
        total_deferred: expenseScheduleData.total_deferred_expense,
        deferred_bill_ids_as_of_current_period: [],
      },
      periodic_recognized_expense: allPeriods.map((period) => {
        const currentSchedule = expenseScheduleData.schedule.find(
          (item) => period === item.period
        );

        return {
          period,
          period_start: currentSchedule?.period_start || "",
          period_end: currentSchedule?.period_end || "",
          recognized_expense:
            currentSchedule?.recognized_expense?.total_recognized || 0,
          deferred_expense:
            currentSchedule?.deferred_expense?.total_deferred || 0,
          recognized_bill_ids: [],
          deferred_bill_ids: [],
        };
      }),
      isTotal: true,
    };

    return [...scheduleRows, totalsRow];
  }, [expenseScheduleData, allPeriods]);

  const onOpen = useCallback((open: boolean, id: string) => {
    setActiveCellId(open ? id : null);
  }, []);

  const columnHelper = createColumnHelper<ExpenseWaterfallRow>();

  const columns = useMemo(() => {
    let fixedColumns = [
      columnHelper.accessor("period", {
        header: "Month",
        cell: (info) => (
          <span
            className={
              info.row.original.isTotal ? "t-font-bold" : "t-font-normal"
            }
          >
            {info.getValue()}
          </span>
        ),
        size: 116,
      }),
      columnHelper.accessor("booked_expense.booked_from_this_period_sum", {
        id: "booked_expense",
        header: () => <div className="t-text-right">Prepaid</div>,
        cell: (info) => (
          <ExpenseWaterfallDetailsModal
            onOpen={onOpen}
            billStartDate={info.row.original.period_start}
            billEndDate={info.row.original.period_end}
            recognitionStartDate={startDate}
            recognitionEndDate={info.row.original.period_end}
            recognitionType="BOOKED"
            header={`Expense pre-paid in ${info.row.original.period}`}
            cell_id={info?.cell?.id}
          >
            <div className="t-text-right">
              <AmountSuperScript amount={Number(info.getValue())} />
            </div>
          </ExpenseWaterfallDetailsModal>
        ),
        size: 116,
      }),
      columnHelper.accessor("recognized_expense.recognized_as_of_period_end", {
        header: () => <div className="t-text-right">Recognised</div>,
        cell: (info) => (
          <ExpenseWaterfallDetailsModal
            onOpen={onOpen}
            billStartDate={info.row.original.period_start}
            billEndDate={info.row.original.period_end}
            recognitionStartDate={info.row.original.period_start}
            recognitionEndDate={values.END_DATE}
            recognitionType="RECOGNIZED"
            header={`Expense booked in ${
              info.row.original.period
            } and recognized as of ${
              info.row.original.periodic_recognized_expense?.at(-1)?.period
            }`}
            cell_id={info.cell.id}
          >
            <div className="t-text-right">
              <AmountSuperScript amount={Number(info.getValue())} />
            </div>
          </ExpenseWaterfallDetailsModal>
        ),
        size: 120,
        id: "recognized_total",
      }),
      ...(values.SHOW_DEFERRALS
        ? [
            columnHelper.accessor(
              "deferred_expense.deferred_expense_as_of_period_end",
              {
                header: () => <div className="t-text-right">Deferred</div>,
                cell: (info) => (
                  <ExpenseWaterfallDetailsModal
                    onOpen={onOpen}
                    billStartDate={info.row.original.period_start}
                    billEndDate={info.row.original.period_end}
                    recognitionStartDate={startDate}
                    recognitionEndDate={info.row.original.period_end}
                    recognitionType="DEFERRED"
                    header={`Expense pre-paid in ${
                      info.row.original.period
                    } and recognized as of ${
                      info.row.original.periodic_recognized_expense?.at(-1)
                        ?.period
                    }`}
                    cell_id={info.cell.id}
                  >
                    <div className="t-text-right">
                      <AmountSuperScript amount={Number(info.getValue())} />
                    </div>
                  </ExpenseWaterfallDetailsModal>
                ),
                size: 116,
                id: "deferred_total",
              }
            ),
          ]
        : []),
    ];

    const asOfEndDateColumnGroup = columnHelper.group({
      id: "fixed_columns",
      header: () => (
        <div className="t-text-center t-text-text-60 t-text-subtext-sm">
          As of{" "}
          {formatTimeToMonthYear(
            expenseScheduleData?.schedule.at(-1)?.period_end
          )}
        </div>
      ),
      columns: fixedColumns as DisplayColumnDef<ExpenseWaterfallRow, unknown>[],
    });

    // Add dynamic periodic columns with grouping
    const periodicColumnGroups = allPeriods.flatMap((period) => {
      const subColumns = [
        columnHelper.display({
          id: `period_${period}_recognized`,
          header: () => (
            <div className="t-text-right">
              {values.SHOW_DEFERRALS ? "Recognised" : period}
            </div>
          ),
          cell: (info) => {
            const periodicData =
              info.row.original.periodic_recognized_expense?.find(
                (p) => p.period === period
              );

            return periodicData ? (
              <ExpenseWaterfallDetailsModal
                onOpen={onOpen}
                billStartDate={info.row.original.period_start}
                billEndDate={info.row.original.period_end}
                recognitionStartDate={periodicData.period_start}
                recognitionEndDate={periodicData.period_end}
                recognitionType="RECOGNIZED"
                header={`Expense pre-paid in ${info.row.original.period} and recognised in ${periodicData.period}`}
                cell_id={info.cell.id}
              >
                <div className="t-text-right">
                  <AmountSuperScript
                    amount={Number(periodicData.recognized_expense)}
                  />
                </div>
              </ExpenseWaterfallDetailsModal>
            ) : (
              <></>
            );
          },
          size: 116,
        }),
      ];

      if (values.SHOW_DEFERRALS) {
        subColumns.push(
          columnHelper.display({
            id: `period_${period}_deferred`,
            header: () => <div className="t-text-right">Deferred</div>,
            cell: (info) => {
              const periodicData =
                info.row.original.periodic_recognized_expense?.find(
                  (p) => p.period === period
                );

              return (
                <div className="t-text-text-60">
                  {periodicData ? (
                    <ExpenseWaterfallDetailsModal
                      onOpen={(open) =>
                        setActiveCellId(open ? info.cell.id : null)
                      }
                      billStartDate={info.row.original.period_start}
                      billEndDate={info.row.original.period_end}
                      recognitionStartDate={periodicData.period_start}
                      recognitionEndDate={periodicData.period_end}
                      recognitionType="DEFERRED"
                      header={`Expense pre-paid in ${info.row.original.period} and deferred in ${periodicData.period}`}
                      cell_id={info.cell.id}
                    >
                      <div className="t-text-right">
                        <AmountSuperScript
                          amount={Number(periodicData.deferred_expense)}
                        />
                      </div>
                    </ExpenseWaterfallDetailsModal>
                  ) : (
                    "-"
                  )}
                </div>
              );
            },
            size: 116,
          })
        );

        return columnHelper.group({
          id: `period_group_${period}`,
          header: () => <div className="t-text-center">{period}</div>,
          columns: subColumns,
        });
      }

      return subColumns;
    });

    if (!values.SHOW_DEFERRALS) {
      const recognisedColumns = columnHelper.group({
        id: "period_group",
        header: () => (
          <div className="t-ml-40 t-text-text-60 t-text-subtext-sm">
            Recognised expense
          </div>
        ),
        columns: periodicColumnGroups as DisplayColumnDef<
          ExpenseWaterfallRow,
          unknown
        >[],
      });

      return {
        withoutDeferrals: [asOfEndDateColumnGroup, recognisedColumns],
        withDeferrals: [],
      };
    }

    return {
      withDeferrals: [asOfEndDateColumnGroup, ...periodicColumnGroups],
      withoutDeferrals: [],
    };
  }, [allPeriods, values.SHOW_DEFERRALS]);

  const table = useReactTable({
    columns: values.SHOW_DEFERRALS
      ? columns.withDeferrals
      : columns.withoutDeferrals,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { minSize: 100 },
    getGroupedRowModel: getGroupedRowModel(),
    enableGrouping: true,
    state: {
      columnPinning: isMobile ? NO_PINNING : DESKTOP_PINNING,
    },
    enableColumnPinning: true,
  });

  const showBorderForStickyHeaders = (columnId: string) => {
    if (!values.SHOW_DEFERRALS) {
      return "period" === columnId;
    }

    return (
      "period" === columnId ||
      (columnId.startsWith("period_") && columnId.endsWith("_deferred"))
    );
  };

  if (!expenseScheduleData) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  const appliedFilterCount =
    (values.START_DATE && values.END_DATE ? 1 : 0) +
    (values.SOURCE?.length ?? 0);

  const onExport = async ({
    startDate,
    endDate,
    showDeferrals,
    includeCarryForward,
    exportToMail,
    reportType,
  }: {
    startDate: string;
    endDate: string;
    showDeferrals: boolean;
    includeCarryForward: boolean;
    exportToMail: boolean;
    reportType: EXPENSE_SCHEDULE_EXPORT_TYPES_TYPE;
  }) => {
    try {
      const downloadLazyGetExpenseSchedule = await lazyGetExpenseSchedule({
        entityId: entityId,
        start_date: startDate,
        end_date: endDate,
        carry_forward_bills: includeCarryForward,
        export_to_mail: exportToMail,
        include_deferrals: showDeferrals,
        report_type: reportType,
        generate_report: true,
      }).unwrap();

      if (exportToMail) {
        successToast({
          message: "Report will be sent to your registered email shortly",
        });
        exportModal.close();
        return;
      }

      if (downloadLazyGetExpenseSchedule.document_id) {
        await downloadFile({
          file_id: downloadLazyGetExpenseSchedule.document_id,
          group_id: groupId,
        });
      }
      successToast({
        message: "Report exported successfully",
      });
      exportModal.close();
    } catch (error) {
      alertToast({
        message: "Failed to export report. Please try again.",
      });
    }
  };

  return (
    <div className="t-space-y-4 t-mt-1">
      {/* Controls */}
      <div className="t-flex t-justify-between t-items-center sm:t-flex-wrap sm:t-gap-3">
        <div className="t-flex t-items-center t-gap-1 sm:t-order-1">
          <Filter.Root
            defaultValue="PERIOD"
            hideDivider={isMobile}
            title={
              isMobile && appliedFilterCount > 0 ? (
                <span className="t-text-text-30">({appliedFilterCount})</span>
              ) : undefined
            }
            capsule={
              <span className="t-contents sm:t-hidden">
                {Boolean(values?.START_DATE) && Boolean(values?.END_DATE) && (
                  <Chip
                    onRemove={() => {}}
                    isRemovable={false}
                    isActive
                    filterType="PERIOD"
                  >
                    {formatTimeToMonthYear(values.START_DATE)}
                    {" to "}
                    {formatTimeToMonthYear(values.END_DATE)}
                  </Chip>
                )}
                {values.SOURCE?.map((s) => (
                  <Chip
                    isActive
                    filterType="SOURCE"
                    onRemove={() =>
                      updateFilter(
                        "SOURCE",
                        values.SOURCE.filter((source) => source !== s)
                      )
                    }
                    key={s}
                  >
                    {REVENUE_DATA_SOURCE_OPTIONS[s]}
                  </Chip>
                ))}
              </span>
            }
          >
            <Filter.Portal>
              <Filter.List>
                <Filter.ListItem value="PERIOD">Period</Filter.ListItem>
                <Filter.ListItem value="SOURCE">Source</Filter.ListItem>
              </Filter.List>
              <Filter.Body value="PERIOD" block>
                <BooksDateFilter
                  pickerType="month"
                  values={values}
                  updateFilter={updateFilter}
                  skipMaxEndDate
                />
              </Filter.Body>
              <Filter.Body value="SOURCE" block>
                <MultiSelectFilter
                  selected={values.SOURCE}
                  onChange={(newValues) =>
                    updateFilter(
                      "SOURCE",
                      newValues as REVENUE_DATA_SOURCE_TYPES[]
                    )
                  }
                  options={sourceOptions}
                />
              </Filter.Body>
            </Filter.Portal>
          </Filter.Root>
        </div>

        <div className="t-flex t-items-center t-gap-6 sm:t-contents">
          <div className="t-flex t-items-center t-gap-2 sm:t-order-3 sm:t-w-full">
            <span className="t-text-body-sm t-text-text-60 t-flex t-items-center t-gap-1">
              <span>Include carry-forward from prior months</span>
              <ToolTip text="Includes expense from older invoices that's still being recognised or deferred in the period selected.This gives a complete picture of total expense activity each month.">
                <span className="t-flex">
                  <Info size="12" stroke="1.4" />
                </span>
              </ToolTip>
            </span>
            <Switch
              checked={values.INCLUDE_CARRY_FORWARD}
              onCheckedChange={(checked) =>
                updateFilter("INCLUDE_CARRY_FORWARD", checked)
              }
              size="small"
            />
          </div>

          <div className="t-flex t-items-center t-gap-2 sm:t-order-3 sm:t-w-full">
            <span className="t-text-body-sm t-text-text-60">
              Show deferrals
            </span>
            <Switch
              checked={values.SHOW_DEFERRALS}
              onCheckedChange={(checked) =>
                updateFilter("SHOW_DEFERRALS", checked)
              }
              size="small"
            />
          </div>

          <Modal.Root
            open={exportModal.isOpen}
            onOpenChange={exportModal.toggle}
          >
            <Modal.Trigger asChild>
              <Button size="xsmall" className="sm:t-order-2 sm:t-ml-auto">
                Export
              </Button>
            </Modal.Trigger>
            <Modal.Content useCustomOverlay>
              <FormikForm
                // @ts-ignore
                onSubmit={() => {}}
                initialValues={{
                  startDate: values.START_DATE,
                  endDate: values.END_DATE,
                  includeCarryForward: values.INCLUDE_CARRY_FORWARD,
                  showDeferrals: values.SHOW_DEFERRALS,
                  reportType: EXPENSE_SCHEDULE_EXPORT_TYPES.MONTHLY,
                }}
              >
                {({ setFieldValue, values, submitForm }) => {
                  return (
                    <>
                      <Modal.Header>
                        <Modal.Title>
                          Export Expense Recognition Report
                        </Modal.Title>
                        <Modal.Close />
                      </Modal.Header>
                      <Modal.Body className="t-flex t-flex-col t-gap-4">
                        <p className="t-mb-2 t-text-body-sm t-text-text-30">
                          Select the options below to customize and export the
                          report.
                        </p>
                        <DateField
                          name="startDate"
                          dateProps={{
                            label: "Start Date",
                            required: true,
                            showMonthYearPicker: true,
                          }}
                        />

                        <DateField
                          name="endDate"
                          dateProps={{
                            label: "End Date",
                            required: true,
                            showMonthYearPicker: true,
                            onDateChange: (date) => {
                              if (date) {
                                const endDate = dayjs(date)
                                  .endOf("month")
                                  .format(YYYY_MM_DD);

                                setFieldValue("endDate", endDate);
                              }
                            },
                          }}
                        />

                        <Combobox
                          required
                          withForm
                          onChange={(option) => {
                            if (option instanceof Array) {
                              return;
                            }
                            if (
                              option?.value ===
                              EXPENSE_SCHEDULE_EXPORT_TYPES.LINE_ITEMS
                            ) {
                              setFieldValue("includeCarryForward", false);
                            }
                          }}
                          name="reportType"
                          label="Group report by"
                          options={convertDisplayToOptions(
                            EXPENSE_SCHEDULE_EXPORT_TYPES_DISPLAY
                          )}
                          value={getCurrentDisplayValue({
                            value: values.reportType,
                            options: convertDisplayToOptions(
                              EXPENSE_SCHEDULE_EXPORT_TYPES_DISPLAY
                            ),
                          })}
                          menuPortalTarget={document.body}
                        />

                        {values.reportType !==
                          EXPENSE_SCHEDULE_EXPORT_TYPES.LINE_ITEMS && (
                          <Checkbox
                            checked={values.includeCarryForward}
                            name="includeCarryForward"
                            onChange={(e) =>
                              setFieldValue(
                                "includeCarryForward",
                                e.target.checked
                              )
                            }
                            label="Include carry-forward from prior months"
                          />
                        )}

                        <Checkbox
                          checked={values.showDeferrals}
                          name="showDeferrals"
                          onChange={(e) =>
                            setFieldValue("showDeferrals", e.target.checked)
                          }
                          label="Include deferrals"
                        />
                      </Modal.Body>
                      <Modal.FooterButtonGroup>
                        <Modal.RawClose asChild>
                          <Button>Cancel</Button>
                        </Modal.RawClose>
                        <Dropdown.Root>
                          <Dropdown.Trigger asChild className="t-group">
                            <span>
                              <Button
                                customType="primary"
                                isLoading={
                                  isExporting ||
                                  isDownloading ||
                                  isFetchingDownload ||
                                  isFetchingExport
                                }
                              >
                                <div className="t-flex t-gap-1 t-items-center">
                                  <span>Export</span>
                                  <div className="group-data-state-open:t-rotate-180 t-ml-2">
                                    <CaretDown />
                                  </div>
                                </div>
                              </Button>
                            </span>
                          </Dropdown.Trigger>
                          <Dropdown.Content
                            align="end"
                            sideOffset={8}
                            side="top"
                          >
                            <ExportOptions
                              // @ts-ignore
                              onLocalDownload={() => onExport(values)}
                              onMailExport={() =>
                                // @ts-ignore
                                onExport({ ...values, exportToMail: true })
                              }
                            />
                          </Dropdown.Content>
                        </Dropdown.Root>
                      </Modal.FooterButtonGroup>
                    </>
                  );
                }}
              </FormikForm>
            </Modal.Content>
          </Modal.Root>
        </div>
      </div>

      {/* Table */}
      <div className="t-overflow-x-auto t-border-neutral-10 t-border-solid t-border t-text-text-60 t-rounded-lg t-overflow-y-hidden">
        <TableUI
          selectedCell={activeCellId}
          sizeType="absolute"
          table={table}
          size="small"
          sticky={false}
          header={({ headerGroup, index }) => (
            <Table.Row className="t-bg-surface-lighter-grey">
              {headerGroup.headers.map((header) => {
                return (
                  <Table.HeadCell
                    colSpan={header.colSpan}
                    isUpperCase={false}
                    key={header.id}
                    className={classNames("!t-text-subtext-sm", {
                      "t-drop-shadow-table-left-pin":
                        (values.SHOW_DEFERRALS
                          ? header.column.id === "deferred_total"
                          : header.column.id === "recognized_total") ||
                        header.column.id === "fixed_columns",
                      // Using drop shadow because border collapsing doesn't allow sticky + border to show
                      "t-drop-shadow-border-shadow-neutral-0":
                        showBorderForStickyHeaders(header.column.id),
                      "!t-z-[1]":
                        showBorderForStickyHeaders(header.column.id) &&
                        !header.column.getIsPinned(),
                      "!t-z-[3]":
                        showBorderForStickyHeaders(header.column.id) &&
                        header.column.getIsPinned(),
                    })}
                    style={{
                      width: `${header.getSize()}px`,
                      minWidth: `${header.column.getSize()}px`,
                      ...(!isMobile &&
                      index === 0 &&
                      header.id === "1_fixed_columns_period"
                        ? {
                            left: 0,
                            position: "sticky",
                            zIndex: 1,
                            backgroundColor: colors.surface["lighter-grey"],
                          }
                        : getCommonPinningStyles(
                            header.column,
                            colors.surface["lighter-grey"],
                            colors.surface["lighter-grey"],
                            2
                          )),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </Table.HeadCell>
                );
              })}
            </Table.Row>
          )}
        >
          {({ row }) => {
            return (
              <Table.Row
                key={row.id}
                className="last:t-bg-surface-lighter-grey [&:not(:last-child)>td]:!t-bg-white [&:last-child>td]:!t-bg-surface-lighter-grey"
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell
                    className={classNames("!t-text-body-sm", {
                      "!t-drop-shadow-table-left-pin": values.SHOW_DEFERRALS
                        ? cell.column.id === "deferred_total"
                        : cell.column.id === "recognized_total",
                      // Using drop shadow because border collapsing doesn't allow sticky + border to show
                      "t-drop-shadow-border-shadow-neutral-0":
                        showBorderForStickyHeaders(cell.column.id),
                      "!t-z-[1]":
                        showBorderForStickyHeaders(cell.column.id) &&
                        !cell.column.getIsPinned(),
                      "!t-z-[3]":
                        showBorderForStickyHeaders(cell.column.id) &&
                        cell.column.getIsPinned(),
                      "neutral-0-important": cell.id === activeCellId,
                    })}
                    key={cell.id}
                    style={{
                      width: `${cell.column.getSize()}px`,
                      minWidth: `${cell.column.getSize()}px`,
                      ...getCommonPinningStyles(
                        cell.column,
                        colors.surface["lighter-grey"],
                        colors.surface["lighter-grey"],
                        2
                      ),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
            );
          }}
        </TableUI>
      </div>
    </div>
  );
};
